/**
 * Key Validation Patterns
 *
 * Validation patterns for common API key formats.
 */

/**
 * Known key patterns for validation
 */
export const KEY_PATTERNS: Record<string, RegExp> = {
  // Stripe
  STRIPE_SECRET_KEY: /^sk_(test|live)_[A-Za-z0-9]+$/,
  STRIPE_WEBHOOK_SECRET: /^whsec_[A-Za-z0-9]+$/,
  STRIPE_PUBLISHABLE_KEY: /^pk_(test|live)_[A-Za-z0-9]+$/,

  // Generic patterns for generated keys
  HEX_64: /^[a-f0-9]{64}$/i,  // 32 bytes
  HEX_32: /^[a-f0-9]{32}$/i,  // 16 bytes

  // OpenAI
  OPENAI_API_KEY: /^sk-[A-Za-z0-9]{48,}$/,

  // Anthropic
  ANTHROPIC_API_KEY: /^sk-ant-[A-Za-z0-9-]+$/,

  // AWS
  AWS_ACCESS_KEY_ID: /^AKIA[A-Z0-9]{16}$/,
  AWS_SECRET_ACCESS_KEY: /^[A-Za-z0-9/+=]{40}$/,

  // Generic patterns
  API_KEY: /^.{10,}$/,  // At least 10 chars
  SECRET_KEY: /^.{10,}$/,
};

/**
 * Key-specific instructions for wizard
 */
export const KEY_INSTRUCTIONS: Record<string, string> = {
  STRIPE_SECRET_KEY: 'Go to https://dashboard.stripe.com/apikeys and click "Roll key" on your secret key',
  STRIPE_WEBHOOK_SECRET: 'Go to https://dashboard.stripe.com/webhooks, select your endpoint, and click "Roll secret"',
  STRIPE_PUBLISHABLE_KEY: 'Go to https://dashboard.stripe.com/apikeys and copy your publishable key',
  OPENAI_API_KEY: 'Go to https://platform.openai.com/api-keys and create a new key',
  ANTHROPIC_API_KEY: 'Go to https://console.anthropic.com/settings/keys and create a new key',
};

/**
 * Get validation pattern for a key name
 */
export function getPatternForKey(keyName: string): RegExp | null {
  // Check exact match first
  if (KEY_PATTERNS[keyName]) {
    return KEY_PATTERNS[keyName];
  }

  // Check if key name contains known prefixes
  // WEBHOOK check must come before generic SECRET check
  if (keyName.includes('STRIPE') && keyName.includes('WEBHOOK')) {
    return KEY_PATTERNS.STRIPE_WEBHOOK_SECRET;
  }
  if (keyName.includes('STRIPE') && keyName.includes('SECRET')) {
    return KEY_PATTERNS.STRIPE_SECRET_KEY;
  }
  if (keyName.includes('STRIPE') && keyName.includes('PUBLISH')) {
    return KEY_PATTERNS.STRIPE_PUBLISHABLE_KEY;
  }
  if (keyName.includes('OPENAI')) {
    return KEY_PATTERNS.OPENAI_API_KEY;
  }
  if (keyName.includes('ANTHROPIC')) {
    return KEY_PATTERNS.ANTHROPIC_API_KEY;
  }

  // Check for encryption key patterns (usually hex)
  if (keyName.includes('ENCRYPTION') || keyName.includes('OAUTH')) {
    return KEY_PATTERNS.HEX_64;
  }

  // Fallback to generic pattern
  return KEY_PATTERNS.API_KEY;
}

/**
 * Get instructions for a key name
 */
export function getInstructionsForKey(keyName: string): string | null {
  return KEY_INSTRUCTIONS[keyName] || null;
}

/**
 * Validate a key value against its expected pattern
 */
export function validateKey(keyName: string, value: string): { valid: boolean; message?: string } {
  const pattern = getPatternForKey(keyName);

  if (!pattern) {
    return { valid: true }; // No validation available
  }

  if (pattern.test(value)) {
    return { valid: true };
  }

  // Provide helpful error messages
  if (keyName.includes('STRIPE') && keyName.includes('SECRET')) {
    return { valid: false, message: 'Stripe secret keys should start with sk_test_ or sk_live_' };
  }
  if (keyName.includes('STRIPE') && keyName.includes('WEBHOOK')) {
    return { valid: false, message: 'Stripe webhook secrets should start with whsec_' };
  }
  if (keyName.includes('ENCRYPTION') || keyName.includes('OAUTH')) {
    return { valid: false, message: 'Expected a 64-character hex string (32 bytes)' };
  }

  return { valid: false, message: 'Value does not match expected format' };
}
